import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { type ReactElement, useRef, useState } from 'react';
import { removeItemByIndex } from '../helpers';
import { SeverityBadge } from './SeverityBadge';

const MATCH_TYPE_OPTIONS = [
  { value: 'exists', label: 'Exists' },
  { value: 'exact', label: 'Exact' },
  { value: 'glob', label: 'Glob' },
  { value: 'arrayContains', label: 'Array Contains' },
  { value: 'lengthGt', label: 'Length >' },
] as const;

const SEVERITY_OPTIONS = [
  { value: 'info', label: 'Info' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
] as const;

/**
 * Well-known VS Code extension manifest fields for validation hints.
 * Not exhaustive — custom/nested fields are still allowed.
 */
const KNOWN_MANIFEST_FIELDS = new Set([
  'name',
  'publisher',
  'version',
  'displayName',
  'description',
  'categories',
  'keywords',
  'engines',
  'main',
  'browser',
  'repository',
  'sponsor',
  'activationEvents',
  'contributes.commands',
  'contributes.configuration',
  'contributes.views',
  'contributes.viewsContainers',
  'contributes.menus',
  'contributes.keybindings',
  'contributes.languages',
  'contributes.grammars',
  'contributes.themes',
  'contributes.snippets',
  'contributes.debuggers',
  'contributes.taskDefinitions',
  'contributes.terminal',
  'contributes.terminal.profiles',
  'contributes.authentication',
  'contributes.customEditors',
  'contributes.notebookRenderer',
  'contributes.chatParticipants',
  'contributes.languageModelTools',
  'contributes.mcpServerDefinitionProviders',
  'contributes.walkthroughs',
  'contributes.typescriptServerPlugins',
  'enabledApiProposals',
  'extensionDependencies',
  'extensionPack',
  'scripts.postinstall',
  'scripts.preinstall',
  'capabilities.untrustedWorkspaces.supported',
]);

export interface CustomCapabilityRule {
  field: string;
  matchType: string;
  value?: string;
  reason: string;
  severity: string;
}

interface CustomCapabilityRulesFormProps {
  value: CustomCapabilityRule[];
  onChange: (value: CustomCapabilityRule[]) => void;
  disabled?: boolean;
}

export function CustomCapabilityRulesForm({
  value,
  onChange,
  disabled = false,
}: CustomCapabilityRulesFormProps): ReactElement {
  const [newRule, setNewRule] = useState<Partial<CustomCapabilityRule>>({
    matchType: 'exists',
    severity: 'info',
  });
  const [fieldWarning, setFieldWarning] = useState<string | null>(null);
  const fieldWarningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const items = Array.isArray(value) ? value : [];

  const needsValue = (matchType: string): boolean => matchType !== 'exists';

  const validateRule = (rule: Partial<CustomCapabilityRule>): boolean => {
    if (!rule.field || rule.field.trim().length === 0) return false;
    if (!rule.reason || rule.reason.trim().length === 0) return false;
    if (!rule.matchType) return false;
    if (
      needsValue(rule.matchType) &&
      (!rule.value || rule.value.trim().length === 0)
    ) {
      return false;
    }
    return true;
  };

  const addRule = (): void => {
    if (!validateRule(newRule)) return;

    const ruleToAdd: CustomCapabilityRule = {
      field: (newRule.field ?? '').trim(),
      matchType: newRule.matchType || 'exists',
      reason: (newRule.reason ?? '').trim(),
      severity: newRule.severity || 'info',
    };

    if (needsValue(ruleToAdd.matchType) && newRule.value) {
      ruleToAdd.value = newRule.value.trim();
    }

    onChange([...items, ruleToAdd]);
    setNewRule({ matchType: 'exists', severity: 'info' });
    setFieldWarning(null);
  };

  const removeRule = (index: number): void => {
    onChange(removeItemByIndex(items, index));
  };

  const updateNewRule = (
    key: keyof CustomCapabilityRule,
    val: string
  ): void => {
    setNewRule(prev => {
      const updated = { ...prev, [key]: val };
      // Clear value when switching to 'exists'
      if (key === 'matchType' && val === 'exists') {
        delete updated.value;
      }
      return updated;
    });

    if (key === 'field') {
      if (fieldWarningTimer.current) {
        clearTimeout(fieldWarningTimer.current);
      }
      if (!val) {
        setFieldWarning(null);
      } else {
        fieldWarningTimer.current = setTimeout(() => {
          if (val && !KNOWN_MANIFEST_FIELDS.has(val)) {
            setFieldWarning(
              'Field not in known manifest list — check spelling. You can still save it.'
            );
          } else {
            setFieldWarning(null);
          }
        }, 600);
      }
    }
  };

  const getMatchTypeLabel = (matchType: string): string =>
    MATCH_TYPE_OPTIONS.find(o => o.value === matchType)?.label ?? matchType;

  const isFindingSeverity = (severity: string): boolean =>
    severity === 'medium' || severity === 'high' || severity === 'critical';

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start gap-4">
        <p className="text-sm text-muted-foreground">
          Add your own rules that match real VSCode manifest fields.{' '}
          <strong>
            Severity Low or Info shows as capability metadata. Severity Medium
            or High creates a security finding (alert type{' '}
            <code>excessive_permissions</code>).
          </strong>{' '}
          Custom rules use their severity directly and are kept separate from
          the built-in weighted score.
        </p>
        <Badge variant="outline" className="whitespace-nowrap">
          {items.length} custom rules
        </Badge>
      </div>

      {/* Add New Rule Form */}
      <div
        className="space-y-2 rounded-md border border-dashed border-border p-3"
        data-testid="custom-capability-rules-add"
      >
        <div className="grid grid-cols-12 gap-2 px-1 text-[11px] font-medium text-foreground/70">
          <div className="col-span-3">Manifest Field</div>
          <div className="col-span-2">Match type</div>
          <div className="col-span-2">Match value (if needed)</div>
          <div className="col-span-2">Reason</div>
          <div className="col-span-2">Severity</div>
          <div className="col-span-1" />
        </div>
        <div className="grid grid-cols-12 gap-2">
          {/* Field */}
          <div className="col-span-3">
            <Input
              value={newRule.field || ''}
              onChange={e => updateNewRule('field', e.target.value)}
              placeholder="e.g., contributes.authentication"
              className="form-input-compact text-xs"
              disabled={disabled}
            />
          </div>
          {/* Match Type */}
          <div className="col-span-2">
            <select
              value={newRule.matchType || 'exists'}
              onChange={e => updateNewRule('matchType', e.target.value)}
              className="form-select-compact text-xs"
              disabled={disabled}
            >
              {MATCH_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {/* Value (conditional) */}
          <div className="col-span-2">
            {needsValue(newRule.matchType || 'exists') ? (
              <Input
                value={newRule.value || ''}
                onChange={e => updateNewRule('value', e.target.value)}
                placeholder={`value required for ${newRule.matchType}`}
                className="form-input-compact text-xs"
                disabled={disabled}
              />
            ) : (
              <div className="h-9 flex items-center text-xs text-muted-foreground px-2">
                —
              </div>
            )}
          </div>
          {/* Reason */}
          <div className="col-span-2">
            <Input
              value={newRule.reason || ''}
              onChange={e => updateNewRule('reason', e.target.value)}
              placeholder="Why this is flagged"
              className="form-input-compact text-xs"
              disabled={disabled}
              maxLength={200}
            />
          </div>
          {/* Severity */}
          <div className="col-span-2">
            <select
              value={newRule.severity || 'info'}
              onChange={e => updateNewRule('severity', e.target.value)}
              className="form-select-compact text-xs"
              disabled={disabled}
            >
              {SEVERITY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {/* Add button */}
          <div className="col-span-1 flex justify-center">
            <Button
              onClick={addRule}
              size="sm"
              type="button"
              className="btn-action-compact"
              disabled={!validateRule(newRule) || disabled}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Field warning */}
        {fieldWarning && (
          <div className="flex items-center gap-1.5 text-[11px] text-yellow-600 dark:text-yellow-400">
            <AlertCircle className="h-3 w-3 shrink-0" />
            <span>{fieldWarning}</span>
          </div>
        )}

        {/* Value required error */}
        {needsValue(newRule.matchType || 'exists') &&
          newRule.field &&
          newRule.reason &&
          !newRule.value && (
            <div className="flex items-center gap-1.5 text-[11px] text-destructive">
              <AlertCircle className="h-3 w-3 shrink-0" />
              <span>
                A match value is required when the match type is glob, exact, or
                arrayContains.
              </span>
            </div>
          )}
      </div>

      {/* Existing rules table */}
      <div className="border border-table-border rounded-md overflow-hidden text-xs">
        {/* Table header */}
        <div className="grid grid-cols-12 gap-1 bg-muted min-h-10 items-center border-b">
          <div className="col-span-3 px-3 font-medium text-foreground/80 whitespace-nowrap">
            Manifest Field
          </div>
          <div className="col-span-2 px-2 font-medium text-foreground/80 whitespace-nowrap">
            Match type
          </div>
          <div className="col-span-2 px-2 font-medium text-foreground/80 whitespace-nowrap">
            Value
          </div>
          <div className="col-span-2 px-2 font-medium text-foreground/80 whitespace-nowrap">
            Reason
          </div>
          <div className="col-span-2 px-2 font-medium text-foreground/80 whitespace-nowrap">
            Severity
          </div>
          <div className="col-span-1"></div>
        </div>

        {items.map((item, index) => (
          <div
            key={`custom-${item.field}-${item.matchType}-${index}`}
            className="grid grid-cols-12 gap-1 min-h-12 items-center border-b last:border-b-0 hover:bg-muted/50"
          >
            <div className="col-span-3 px-3 py-2 truncate">
              <Tooltip>
                <TooltipTrigger asChild>
                  <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded cursor-default">
                    {item.field}
                  </code>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{item.field}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="col-span-2 px-2 py-2">
              <Badge variant="outline" className="text-[10px]">
                {getMatchTypeLabel(item.matchType)}
              </Badge>
            </div>
            <div className="col-span-2 px-2 py-2 truncate text-muted-foreground">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{item.value || '—'}</span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{item.value || '—'}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="col-span-2 px-2 py-2 truncate text-muted-foreground">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{item.reason}</span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="max-w-xs">{item.reason}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="col-span-2 px-2 py-2 flex flex-col gap-0.5">
              <SeverityBadge
                riskLevel={item.severity}
                showIcon
                className="text-xs"
              />
              <span className="text-[10px] text-muted-foreground">
                {isFindingSeverity(item.severity)
                  ? 'Security finding'
                  : 'Capability metadata'}
              </span>
            </div>
            <div className="col-span-1 px-2 py-2 flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => removeRule(index)}
                className="h-7 w-7 p-0"
                disabled={disabled}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}

        {/* Empty state when no custom rules */}
        {items.length === 0 && (
          <div className="px-3 py-6 text-center text-muted-foreground">
            No custom rules configured
          </div>
        )}
      </div>

      <div className="text-xs text-muted-foreground">
        {items.length} custom rules configured
      </div>

      {/* Inline help text */}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground">
          Which manifest fields can I target?
        </summary>
        <div className="mt-2 space-y-1 pl-3 border-l-2 border-muted">
          <p>
            Rules evaluate fields from the VS Code extension manifest
            (package.json). Common fields:
          </p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>
              <code className="text-[10px]">activationEvents</code> — when the
              extension activates
            </li>
            <li>
              <code className="text-[10px]">contributes.*</code> — registered
              capabilities (authentication, debuggers, chatParticipants, etc.)
            </li>
            <li>
              <code className="text-[10px]">enabledApiProposals</code> —
              proposed APIs the extension opts into
            </li>
            <li>
              <code className="text-[10px]">extensionDependencies</code> —
              required extensions that are force-installed
            </li>
            <li>
              <code className="text-[10px]">extensionPack</code> — bundled
              extensions
            </li>
            <li>
              <code className="text-[10px]">scripts.*</code> — npm lifecycle
              scripts (postinstall, preinstall)
            </li>
            <li>
              <code className="text-[10px]">capabilities.*</code> — workspace
              trust declarations
            </li>
          </ul>
          <p className="mt-1">
            Use dot-notation for nested fields (e.g.,{' '}
            <code className="text-[10px]">contributes.authentication</code>).
          </p>
        </div>
      </details>
    </div>
  );
}
